import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Plus, Search, Edit, Trash2, Eye, Send, 
  Globe, Calendar, Tag, Loader2, CheckCircle, Clock, Archive,
  Filter, MoreVertical
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CATEGORIES = [
  { value: 'General', label: 'General' },
  { value: 'HR & Employment', label: 'HR & Employment' },
  { value: 'Health & Safety', label: 'Health & Safety' },
  { value: 'IT & Security', label: 'IT & Security' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Code of Conduct', label: 'Code of Conduct' },
  { value: 'Leave & Attendance', label: 'Leave & Attendance' },
  { value: 'Other', label: 'Other' },
];

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-800',
};

const PolicyManagement = () => {
  const [policies, setPolicies] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    category: 'General',
    office_id: 'ALL',
    version: '1.0',
    effective_date: '',
    tags: '',
    requires_acknowledgment: false,
  });

  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPolicies();
    fetchOffices();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/policies/admin`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPolicies(data);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/offices`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setOffices(data);
      }
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  };

  const handleCreate = async () => {
    setProcessing(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        effective_date: formData.effective_date || null,
      };

      const response = await fetch(`${API_URL}/api/policies/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowCreateModal(false);
        resetForm();
        fetchPolicies();
      }
    } catch (error) {
      console.error('Error creating policy:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPolicy) return;
    setProcessing(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        effective_date: formData.effective_date || null,
      };

      const response = await fetch(`${API_URL}/api/policies/admin/${selectedPolicy.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowEditModal(false);
        resetForm();
        fetchPolicies();
      }
    } catch (error) {
      console.error('Error updating policy:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handlePublish = async (policy) => {
    try {
      const response = await fetch(`${API_URL}/api/policies/admin/${policy.id}/publish`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        fetchPolicies();
      }
    } catch (error) {
      console.error('Error publishing policy:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedPolicy) return;
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/policies/admin/${selectedPolicy.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedPolicy(null);
        fetchPolicies();
      }
    } catch (error) {
      console.error('Error deleting policy:', error);
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      summary: '',
      category: 'General',
      office_id: 'ALL',
      version: '1.0',
      effective_date: '',
      tags: '',
      requires_acknowledgment: false,
    });
    setSelectedPolicy(null);
  };

  const openEditModal = (policy) => {
    setSelectedPolicy(policy);
    setFormData({
      title: policy.title,
      content: policy.content,
      summary: policy.summary || '',
      category: policy.category,
      office_id: policy.office_id,
      version: policy.version,
      effective_date: policy.effective_date ? policy.effective_date.split('T')[0] : '',
      tags: policy.tags?.join(', ') || '',
      requires_acknowledgment: policy.requires_acknowledgment,
    });
    setShowEditModal(true);
  };

  const openViewModal = (policy) => {
    setSelectedPolicy(policy);
    setShowViewModal(true);
  };

  const openDeleteModal = (policy) => {
    setSelectedPolicy(policy);
    setShowDeleteModal(true);
  };

  // Filter policies
  const filteredPolicies = policies.filter(policy => {
    // Status filter
    if (activeTab !== 'all' && policy.status !== activeTab) return false;
    
    // Search filter
    if (searchQuery && !policy.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    
    // Office filter
    if (selectedOffice !== 'all' && policy.office_id !== selectedOffice) return false;
    
    // Category filter
    if (selectedCategory !== 'all' && policy.category !== selectedCategory) return false;
    
    return true;
  });

  // Stats
  const stats = {
    total: policies.length,
    published: policies.filter(p => p.status === 'published').length,
    draft: policies.filter(p => p.status === 'draft').length,
    archived: policies.filter(p => p.status === 'archived').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Policies</h1>
          <p className="text-gray-600">Create and manage policies for different offices</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-policy-button">
          <Plus className="h-4 w-4 mr-2" />
          Create Policy
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Policies</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Published</p>
                <p className="text-2xl font-bold">{stats.published}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Drafts</p>
                <p className="text-2xl font-bold">{stats.draft}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Archive className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Archived</p>
                <p className="text-2xl font-bold">{stats.archived}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search policies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-policies-input"
                />
              </div>
            </div>
            <Select value={selectedOffice} onValueChange={setSelectedOffice}>
              <SelectTrigger className="w-[180px]" data-testid="filter-office-select">
                <Globe className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Offices" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Offices</SelectItem>
                {offices.map(office => (
                  <SelectItem key={office.id} value={office.id}>{office.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category-select">
                <Tag className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs and Policy List */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
          <TabsTrigger value="published">Published ({stats.published})</TabsTrigger>
          <TabsTrigger value="draft">Drafts ({stats.draft})</TabsTrigger>
          <TabsTrigger value="archived">Archived ({stats.archived})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredPolicies.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No policies found</h3>
                <p className="text-gray-500 mt-1">Create your first policy to get started</p>
                <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Policy
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredPolicies.map(policy => (
                <Card key={policy.id} className="hover:shadow-md transition-shadow" data-testid={`policy-card-${policy.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{policy.title}</h3>
                          <Badge className={STATUS_COLORS[policy.status]}>
                            {policy.status}
                          </Badge>
                        </div>
                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                          {policy.summary || policy.content.substring(0, 150)}...
                        </p>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Globe className="h-4 w-4" />
                            {policy.office_name || policy.office_id}
                          </span>
                          <span className="flex items-center gap-1">
                            <Tag className="h-4 w-4" />
                            {policy.category}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(policy.updated_at).toLocaleDateString()}
                          </span>
                          {policy.created_by_name && (
                            <span>By {policy.created_by_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => openViewModal(policy)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditModal(policy)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {policy.status === 'draft' && (
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handlePublish(policy)}
                            data-testid={`publish-policy-${policy.id}`}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Publish
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => openDeleteModal(policy)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="policy-form-modal">
          <DialogHeader>
            <DialogTitle>
              {showEditModal ? 'Edit Policy' : 'Create New Policy'}
            </DialogTitle>
            <DialogDescription>
              {showEditModal ? 'Update the policy details below' : 'Fill in the details to create a new company policy'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title">Policy Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Remote Work Policy"
                  data-testid="policy-title-input"
                />
              </div>

              <div>
                <Label htmlFor="office">Office *</Label>
                <Select value={formData.office_id} onValueChange={(val) => setFormData({ ...formData, office_id: val })}>
                  <SelectTrigger data-testid="policy-office-select">
                    <SelectValue placeholder="Select office" />
                  </SelectTrigger>
                  <SelectContent>
                    {offices.map(office => (
                      <SelectItem key={office.id} value={office.id}>{office.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger data-testid="policy-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0"
                />
              </div>

              <div>
                <Label htmlFor="effective_date">Effective Date</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="summary">Summary</Label>
                <Input
                  id="summary"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Brief description of the policy"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="content">Policy Content *</Label>
                <div className="border rounded-md overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="Enter the full policy content here..."
                    style={{ minHeight: '250px' }}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        [{ 'align': [] }],
                        ['link'],
                        ['clean']
                      ],
                    }}
                    formats={[
                      'header', 'bold', 'italic', 'underline', 'strike',
                      'list', 'bullet', 'indent', 'align', 'link'
                    ]}
                  />
                </div>
              </div>

              <div className="col-span-2">
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g., remote, work-from-home, flexible"
                />
              </div>

              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_acknowledgment"
                  checked={formData.requires_acknowledgment}
                  onChange={(e) => setFormData({ ...formData, requires_acknowledgment: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="requires_acknowledgment" className="font-normal">
                  Require employees to acknowledge reading this policy
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={showEditModal ? handleUpdate : handleCreate}
              disabled={!formData.title || !formData.content || processing}
              data-testid="save-policy-button"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {showEditModal ? 'Update Policy' : 'Create Policy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPolicy?.title}
              <Badge className={STATUS_COLORS[selectedPolicy?.status]}>
                {selectedPolicy?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedPolicy && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm text-gray-500 pb-4 border-b">
                <span className="flex items-center gap-1">
                  <Globe className="h-4 w-4" />
                  {selectedPolicy.office_name || selectedPolicy.office_id}
                </span>
                <span className="flex items-center gap-1">
                  <Tag className="h-4 w-4" />
                  {selectedPolicy.category}
                </span>
                <span>Version {selectedPolicy.version}</span>
                {selectedPolicy.effective_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Effective: {new Date(selectedPolicy.effective_date).toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="prose max-w-none">
                <div 
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{ __html: selectedPolicy.content }}
                />
              </div>

              {selectedPolicy.tags?.length > 0 && (
                <div className="flex gap-2 pt-4 border-t">
                  {selectedPolicy.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline">{tag}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowViewModal(false);
              openEditModal(selectedPolicy);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent data-testid="delete-policy-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Policy</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this policy? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {selectedPolicy && (
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="font-medium">{selectedPolicy.title}</p>
              <p className="text-sm text-gray-600">{selectedPolicy.office_name} - {selectedPolicy.category}</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={processing}
              data-testid="confirm-delete-policy"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PolicyManagement;
